import React, { useEffect, useMemo } from 'react';

import { V1CloudEventsResourceType } from '@ariksa/audittrail/api';
import dayjs from 'dayjs';
import { DateRange } from 'react-day-picker';
import { useDispatch, useSelector } from 'react-redux';

import { CDashboard } from 'components/Visualization';
import { useAccessBoundary } from 'containers/App/hooks/useAccessBoundary';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';
import { actions } from 'containers/Visibility/IncidentResponse/slice';

import { selectIncidentResponse } from '../../../selectors';

import { getCloudEventsSummaryConfig } from './config';
import { formatCloudEventsSummaryDateRange } from './utils';

interface CloudEventsResourceSummaryProps {
  accountId?: string;
  dateRange: DateRange;
  resourceArn?: string;
  resourceType?: string;
}

export const CloudEventsResourceSummary = ({
  accountId,
  dateRange,
  resourceArn,
  resourceType,
}: CloudEventsResourceSummaryProps) => {
  const dispatch = useDispatch();
  const { environmentId } = useAccessBoundary();
  const { resourceSummary } = useSelector(selectIncidentResponse);
  const dashboardConfig = useMemo(
    () =>
      getCloudEventsSummaryConfig(
        resourceSummary,
        formatCloudEventsSummaryDateRange(dateRange),
      ),
    [dateRange, resourceSummary],
  );
  const { accountMapping } = useCloudAccountId();

  useEffect(() => {
    if (!resourceArn || !dateRange.from || !dateRange.to) return;
    const account = accountMapping?.[accountId!]?.cloud_account_id || undefined;

    dispatch(
      actions.getCloudEventsResourceSummary({
        q: {
          body: {
            start_date: dayjs(dateRange.from).format('YYYY-MM-DD'),
            end_date: dayjs(dateRange.to).format('YYYY-MM-DD'),
            account_id: account,
            environment_id: environmentId,
            resource_arn: resourceArn,
            resource_type:
              (resourceType as V1CloudEventsResourceType) ??
              V1CloudEventsResourceType.SimpleStorageService,
          },
        },
      }),
    );
  }, [
    accountId,
    dateRange.from,
    dateRange.to,
    dispatch,
    environmentId,
    resourceArn,
    resourceType,
    accountMapping,
  ]);

  return <CDashboard dashboardWidgets={dashboardConfig} />;
};
